import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

import * as actions from "../../store/actions/index";
import { withRouter } from "../../shared/utility";

// Template Components
import DataManagementTemplate from "../../components/templates/DataManagementTemplate";
import MobileCardsTemplate from "../../components/templates/MobileCardsTemplate";

// Other Components
import SlideOver from "../../components/slideOver/SlideOver";
import EmptyModal from "../../components/modals/EmptyModal";
import { CustomerGroupsViewModel } from "../../viewmodels/customerGroups/CustomerGroupsViewModel";
import type { DataColumn } from "../../components/templates/DataManagementTemplate";
import type { PaginatorPageChangeEvent } from "primereact/paginator";
import type { AppDispatch, RootState } from "../../index";
import type { CustomerGroup } from "../../types/customerGroups";

interface CustomerGroupsProps { logout: () => void }

function CustomerGroups(props: CustomerGroupsProps) {
  const { logout } = props;
  const navigate = useNavigate();
  const {
    emptyModal,
    setEmptyModelOpen,
    getCustomerGroups,
    openDeleteCustomerGroupModal,
    loading,
    customerGroups,
    setSlideOver,
    slideOver,
  } = CustomerGroupsViewModel(logout);

  var currentPage = 1;
  useEffect(() => {
    getCustomerGroups(null, currentPage);
  }, []);

  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(50);
  const [searchTerm, setSearchTerm] = useState("");

  // Search handler
  const handleSearch = (term: string) => {
    setSearchTerm(term);
    getCustomerGroups(term || null, currentPage);
  };

  // Pagination handler
  const handlePageChange = (event: PaginatorPageChangeEvent) => {
    currentPage = event.page + 1;
    setFirst(event.first);
    setRows(event.rows);
    getCustomerGroups(searchTerm || null, currentPage);
  };

  // Row click handler
  const handleRowClick = (group: CustomerGroup) => {
    navigate(`/customer-groups/${group.id}`);
  };

  // Button configurations
  const headerButtons = [
    {
      label: "Add Group",
      onClick: () => navigate("/customer-groups/new"),
      className:
        "bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white focus:ring-primary-500",
    },
  ];

  // Column configurations for DataTable
  const columns: DataColumn<CustomerGroup>[] = [
    {
      field: "name",
      header: "Group Name",
      style: { width: "40%" },
      body: (rowData) => (
        <span className="font-semibold apollo-subheading text-gray-900">{rowData.name}</span>
      ),
    },
    {
      header: "Customers in group",
      style: { width: "20%", textAlign: "start" },
      body: (rowData) => <span className="apollo-body">{rowData.customers_count} customer(s)</span>,
    },
    {
      header: "Actions",
      style: { width: "20%" },
      headerClassName: "customer-groups-actions-col",
      className: "customer-groups-actions-col",
      body: (rowData) => (
        <div className="flex flex-wrap items-center justify-center gap-2 w-full">
          <Link
            className="inline-flex items-center justify-center px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            to={`/customer-groups/${rowData.id}`}
            onClick={(e) => e.stopPropagation()}
          >
            View
          </Link>
          <button
            type="button"
            className="inline-flex items-center justify-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            onClick={(e) => {
              e.stopPropagation();
              openDeleteCustomerGroupModal(rowData);
            }}
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  // Mobile cards component
  const CustomerGroupsMobileCards = ({ data }: { data: CustomerGroup[] }) => (
    <MobileCardsTemplate
      data={data}
      onCardClick={(group) => navigate(`/customer-groups/${group.id}`)}
      onEdit={(group) => navigate(`/customer-groups/${group.id}`)}
      editRoute="/customer-groups"
      emptyMessage="No customer groups found"
      cardContent={(group) => (
        <div className="flex flex-col space-y-3">
          {/* Header */}
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-h6 apollo-heading text-primary-600 mb-1">{group.name}</h3>
              <p className="text-body-2 apollo-subheading">
                {group.total_in_group} customers in group
              </p>
            </div>
          </div>
        </div>
      )}
      actionButtons={(group) => (
        <div className="flex gap-2 pt-2" onClick={(e) => e.stopPropagation()}>
          <Link to={`/customer-groups/${group.id}`} className="flex-1">
            <button
              type="button"
              className="w-full inline-flex items-center justify-center px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              View
            </button>
          </Link>
          <button
            type="button"
            className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            onClick={() => openDeleteCustomerGroupModal(group)}
          >
            Delete
          </button>
        </div>
      )}
    />
  );

  return (
    <div>
      <DataManagementTemplate
        title="Customer Groups"
        description="Manage your customer groups"
        headerButtons={headerButtons}
        columns={columns}
        data={customerGroups.data || []}
        loading={loading}
        pagination={{
          first: first,
          rows: rows,
          total: customerGroups.total || 0,
          template:
            "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport",
          currentPageReportTemplate: "Showing {first} to {last} of {totalRecords} customer groups",
        }}
        onSearch={handleSearch}
        onPageChange={handlePageChange}
        onRowClick={handleRowClick}
        searchPlaceholder="Search by customer group name"
        MobileCardsComponent={CustomerGroupsMobileCards}
        emptyMessage="No customer groups found"
      />
      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={setEmptyModelOpen}
      />
      <SlideOver
        childComponent={slideOver.childComponent}
        slideOver={slideOver}
        setSlideOver={setSlideOver}
      />
    </div>
  );
}

const mapStateToProps = (_state: RootState) => {
  return {};
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    logout: () => dispatch(actions.logout()),
  };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(CustomerGroups));
